const normalizedText = (value: string | null | undefined): string =>
  String(value ?? '').replace(/\s+/g, ' ').trim().toLocaleLowerCase();

function findPanel(root: HTMLElement, phrases: string[]): HTMLElement | null {
  const labels = Array.from(
    root.querySelectorAll<HTMLElement>('h1, h2, h3, h4, .eyebrow')
  );

  for (const phrase of phrases) {
    const match = labels.find((label) =>
      normalizedText(label.textContent).includes(phrase)
    );
    if (!match) continue;

    return (
      match.closest<HTMLElement>('section, article, [data-admin-panel]') ??
      match.parentElement
    );
  }

  return null;
}

function neighborhoodCards(panel: HTMLElement): HTMLElement[] {
  const interactive = Array.from(
    panel.querySelectorAll<HTMLElement>('button, a, [role="button"]')
  );

  let cards = interactive.filter((element) => {
    const attributes = Array.from(element.attributes)
      .map((attribute) => `${attribute.name}=${attribute.value}`)
      .join(' ');
    const metadata = normalizedText(`${element.className} ${attributes}`);
    return metadata.includes('neighborhood') && !metadata.includes('search');
  });

  cards = cards.filter(
    (card) => !cards.some((other) => other !== card && other.contains(card))
  );

  if (cards.length >= 2) return cards;

  const grid = Array.from(
    panel.querySelectorAll<HTMLElement>('[class*="grid"], [class*="list"]')
  ).find((element) => {
    const metadata = normalizedText(element.className);
    return (
      element.children.length >= 2 &&
      (metadata.includes('neighborhood') || metadata.includes('coverage'))
    );
  });

  return grid
    ? Array.from(grid.children).filter(
        (child): child is HTMLElement => child instanceof HTMLElement
      )
    : [];
}

function enhanceNeighborhoodSearch(panel: HTMLElement): void {
  if (panel.querySelector('#coverage-neighborhood-search')) return;
  if (panel.dataset.neighborhoodSearchEnhanced === 'true') return;

  const cards = neighborhoodCards(panel);
  if (cards.length < 2) return;

  const container = cards[0]!.parentElement;
  if (!container || !cards.every((card) => card.parentElement === container)) {
    return;
  }

  panel.dataset.neighborhoodSearchEnhanced = 'true';
  container.classList.add('admin-neighborhood-search-results');

  const searchPanel = document.createElement('div');
  searchPanel.className = 'admin-neighborhood-search-panel';
  searchPanel.innerHTML = `
    <label class="admin-neighborhood-search-label" for="admin-neighborhood-search">
      Search neighborhood
    </label>
    <div class="admin-neighborhood-search-row">
      <input
        id="admin-neighborhood-search"
        class="admin-neighborhood-search-input"
        type="search"
        autocomplete="off"
        spellcheck="false"
        placeholder="Start typing a neighborhood name…"
      />
      <button
        class="admin-neighborhood-search-clear"
        type="button"
        aria-label="Clear neighborhood search"
      >Clear</button>
    </div>
    <p class="admin-neighborhood-search-status" role="status"></p>
  `;

  container.parentElement?.insertBefore(searchPanel, container);

  const input = searchPanel.querySelector<HTMLInputElement>(
    '.admin-neighborhood-search-input'
  );
  const clear = searchPanel.querySelector<HTMLButtonElement>(
    '.admin-neighborhood-search-clear'
  );
  const status = searchPanel.querySelector<HTMLElement>(
    '.admin-neighborhood-search-status'
  );

  if (!input || !clear || !status) return;

  const entries = cards.map((card) => ({
    card,
    label: normalizedText(card.textContent)
  }));
  const applySearch = (): void => {
    const query = normalizedText(input.value);

    const matches = query
      ? entries.filter(({ label }) => label.includes(query))
      : entries;

    const visibleCards = new Set(matches.map(({ card }) => card));

    for (const { card } of entries) {
      const visible = visibleCards.has(card);
      card.hidden = !visible;
      card.classList.toggle('hidden', !visible);
      card.toggleAttribute('data-filter-hidden', !visible);
      card.setAttribute('aria-hidden', visible ? 'false' : 'true');
      if (visible) card.style.removeProperty('display');
      else card.style.setProperty('display', 'none', 'important');
      card.classList.add('admin-neighborhood-search-result');
    }

    container.hidden = matches.length === 0;
    if (matches.length === 0) container.style.setProperty('display', 'none', 'important');
    else container.style.removeProperty('display');
    clear.disabled = !query;
    status.textContent = matches.length
      ? `${matches.length.toLocaleString()} neighborhood${matches.length === 1 ? '' : 's'} shown.`
      : 'No neighborhoods match that search.';
  };

  input.addEventListener('input', applySearch);
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      input.value = '';
      applySearch();
      input.focus();
      return;
    }

    if (event.key === 'Enter') {
      const firstVisible = entries.find(({ card }) => !card.hidden)?.card;
      if (firstVisible) {
        event.preventDefault();
        firstVisible.click();
      }
    }
  });

  clear.addEventListener('click', () => {
    input.value = '';
    applySearch();
    input.focus();
  });

  applySearch();
}

function enhanceAdminDashboard(): void {
  const main = document.querySelector<HTMLElement>('.admin-main');
  if (!main) return;
  const coverage = findPanel(main, ['coverage by neighborhood']);
  if (coverage) enhanceNeighborhoodSearch(coverage);
}

export function installAdminDashboardLayout(): void {
  let queued = false;

  const schedule = (): void => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => {
      queued = false;
      enhanceAdminDashboard();
    });
  };

  const observer = new MutationObserver(schedule);
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true });
  } else {
    schedule();
  }
}
